import Badge from "@common/controls/Badge/component";
import { NO_VALUE, getFormattedValue, getWebsiteValue } from "@common/controls/Grid/lib";
import Hint from "@common/controls/Hint/component";
import ExternalLink from "@common/controls/Icon/ExternalLink/component";
import HelpIcon from "@common/controls/Icon/Help/component";
import Icon from "@common/controls/Icon/component";
import { IconType, SystemColor } from "@common/declarations";
import { type I18n } from "@common/lib/i18n";
import { joinDefined } from "@common/lib/misc";
import { getHostName } from "@common/lib/url";
import { isFunction } from "lodash";
import React from "react";
import { type GridColumn } from "../types";
import OptionsWithShowMore from "./OptionsWithShowMore";

export const renderTooltip = <T extends {}>(col: GridColumn<T>, data: T) => {
  const tooltip = isFunction(col.tooltip) ? col.tooltip(data) : col.tooltip;
  if (!tooltip) return null;

  return typeof tooltip === "string" ? (
    <HelpIcon tooltip={tooltip} />
  ) : (
    <Icon icon={tooltip.icon ?? IconType.Info} color={tooltip.color} tooltip={tooltip.data} />
  );
};

export const renderCellValue = <T extends {}>(i18n: I18n, col: GridColumn<T>, data: T): any => {
  const value = col.data ? col.data(data) : getFormattedValue(i18n, col, data);

  switch (col.dataType) {
    case "active":
    case "published":
    case "boolean":
    case "enabled": {
      return (
        <Badge color={data[col.key] ? SystemColor.Green : SystemColor.Gray}>
          {value} {renderTooltip(col, data)}
        </Badge>
      );
    }
    case "archived": {
      return (
        <Badge color={data[col.key] ? SystemColor.Gray : SystemColor.Green}>
          {value} {renderTooltip(col, data)}
        </Badge>
      );
    }
    case "tickCross": {
      return (
        <Icon
          icon={data[col.key] ? IconType.Tick : IconType.CrossCircle}
          color={data[col.key] ? SystemColor.Green : SystemColor.Red}
        />
      );
    }
    case "showMore": {
      return Array.isArray(value) && value.length ? <OptionsWithShowMore data={value} /> : NO_VALUE;
    }
    case "website": {
      const v = getWebsiteValue(data);
      if (v) {
        return (
          <span className="single-line-ellipsis">
            {v.name}&nbsp;
            <Hint
              hint={v.domain ? `(${joinDefined(".", v.subdomain, v.domain)})` : `(${i18n.noDomain.toLowerCase()})`}
            />
            {renderTooltip(col, data)}
          </span>
        );
      }
      return value ? value : NO_VALUE;
    }
    case "url": {
      return value ? (
        <>
          <span>
            {getHostName(value)}

            {renderTooltip(col, data)}
          </span>
          <ExternalLink onClick={() => window.open(value.startsWith("http") ? value : `https://${value}`)} />
        </>
      ) : (
        NO_VALUE
      );
    }
    case "yesNo": {
      return (
        <Badge color={data[col.key] ? SystemColor.Green : SystemColor.Gray}>
          {data[col.key] ? i18n.yes : i18n.no} {renderTooltip(col, data)}
        </Badge>
      );
    }
    default: {
      return (
        <span className="cell_value single-line-ellipsis" title={typeof value === "string" ? value : undefined}>
          {typeof value === "string" ? (
            // NOTE: we use || to display NO_VALUE in case of an empty string
            <span dangerouslySetInnerHTML={{ __html: value || NO_VALUE }} />
          ) : (
            value ?? NO_VALUE
          )}

          {renderTooltip(col, data)}
        </span>
      );
    }
  }
};
